import { useState } from "react";
import { Autocomplete, Badge, Box, Button, Card, Group, RingProgress, Skeleton, Stack, Text, TextInput } from "@mantine/core";
import { useMutation } from "@tanstack/react-query";
import { fmtNumber, type PassioFood } from "@passio/api-client";
import { passio } from "../../lib/clients";
import { useSession } from "../../lib/session";
import { SAMPLE_BARCODES } from "../../lib/samples";
import { ErrorNotice, FoodIcon } from "../../components/common";
import { GOAL_PRESETS, GoalRatingResult } from "../../components/GoalRating";
import { RunBar, Step, TourTimeline } from "./shared";

/** Product name + brand + label ingredients: the text the AI tools get for a scanned product. */
export function productText(p: PassioFood): string {
  const brand = p.branded?.owner ? ` by ${p.branded.owner}` : "";
  const ingredients = p.branded?.ingredients ? `. Ingredients: ${p.branded.ingredients}` : "";
  return `${p.name ?? "Packaged product"}${brand}${ingredients}`;
}

const mid = (a?: number, b?: number) => ((a ?? 0) + (b ?? a ?? 0)) / 2;

/** Packaged food flow: barcode to product, then score the label with the AI tools. */
export function BarcodeTour({ onOpen }: { onOpen: (food: PassioFood) => void }) {
  const { hasFeature } = useSession();
  const [code, setCode] = useState(SAMPLE_BARCODES[2].code);
  const [goal, setGoal] = useState(GOAL_PRESETS[0]);
  const lookup = useMutation({ mutationFn: (c: string) => passio.foods.byProductCode(c) });
  const upf = useMutation({ mutationFn: (t: string) => passio.tools.ultraProcessedFoodRating(t) });
  const gi = useMutation({ mutationFn: (t: string) => passio.tools.estimateGlycemicIndexLoad(t) });
  const rate = useMutation({ mutationFn: (v: { food: string; goal: string }) => passio.tools.genericFoodRating(v.food, v.goal) });
  const [running, setRunning] = useState(false);

  const can = { lookup: hasFeature("lookup"), insights: hasFeature("insights") };
  const product = lookup.data?.data;

  const reset = () => [lookup, upf, gi, rate].forEach((m) => m.reset());

  const runAll = async () => {
    reset();
    setRunning(true);
    try {
      const p = (await lookup.mutateAsync(code.replace(/\D/g, ""))).data;
      if (can.insights) {
        const text = productText(p);
        await Promise.allSettled([upf.mutateAsync(text), gi.mutateAsync(p.name ?? text), ...(goal.trim() ? [rate.mutateAsync({ food: text, goal: goal.trim() })] : [])]);
      }
    } catch {
      // Each step shows its own error.
    } finally {
      setRunning(false);
    }
  };

  const busy = running || lookup.isPending;
  const active = rate.data ? 4 : gi.data ? 3 : upf.data ? 2 : lookup.data ? 1 : 0;
  const rating = upf.data?.data.rating;
  const giRow = gi.data?.data.ingredients?.[0];
  const locked = can.insights ? undefined : "Food insights aren't enabled for this account.";

  if (!can.lookup) {
    return (
      <Card>
        <Text c="dimmed">Barcode lookups aren't enabled for this account, so the barcode tour can't run.</Text>
      </Card>
    );
  }

  return (
    <Stack gap="lg">
      <Card p={{ base: "md", sm: "xl" }}>
        <Stack gap="sm">
          <TextInput label="Barcode (UPC / EAN)" size="md" value={code} onChange={(e) => setCode(e.currentTarget.value)} disabled={busy} inputMode="numeric" />
          <Group gap={6}>
            {SAMPLE_BARCODES.map((s) => (
              <Badge key={s.code} variant={code === s.code ? "filled" : "outline"} color="gray" tt="none" style={{ cursor: "pointer" }} onClick={() => !busy && setCode(s.code)}>
                {s.label}
              </Badge>
            ))}
          </Group>
          <Autocomplete label="Your goal" description="Step 4 rates the product against it." data={GOAL_PRESETS} value={goal} onChange={setGoal} disabled={busy} />
          <RunBar busy={busy} canRun={code.replace(/\D/g, "").length >= 6} hasResults={active > 0} onRun={runAll} onClear={reset} />
        </Stack>
      </Card>

      <TourTimeline
        active={active}
        steps={[
          {
            label: "Scan",
            body: (
              <Step title="Barcode to product" route="GET /napi/food/productCode/{productCode}" to="/barcode" done={Boolean(lookup.data)}>
                {lookup.error ? <ErrorNotice error={lookup.error} /> : null}
                {lookup.isPending && <Skeleton h={80} radius="md" />}
                {product && !lookup.isPending && (
                  <Group align="flex-start" wrap="nowrap" gap="md">
                    <FoodIcon iconId={product.iconId} src={product.images?.product || undefined} alt="" size={56} />
                    <Stack gap={4} style={{ minWidth: 0 }}>
                      <Text fw={700}>{product.name}</Text>
                      {product.branded?.owner && (
                        <Text fz="sm" c="dimmed">
                          {product.branded.owner}
                        </Text>
                      )}
                      {product.branded?.ingredients && (
                        <Text fz="xs" c="dimmed" lineClamp={3}>
                          {product.branded.ingredients}
                        </Text>
                      )}
                      <Box>
                        <Button size="compact-xs" variant="subtle" color="ink" onClick={() => onOpen(product)}>
                          Full nutrition
                        </Button>
                      </Box>
                    </Stack>
                  </Group>
                )}
                {!lookup.data && !lookup.isPending && !lookup.error && (
                  <Text fz="sm" c="dimmed">
                    A UPC or EAN becomes a packaged food with label nutrition, ingredients and allergens. Its name, brand and ingredient list feed the next steps.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "How processed is it?",
            body: (
              <Step title="Ultra-processed rating from the label" route="POST /napi/tools/ultraProcessedFoodRating" to="/insights" done={Boolean(upf.data)} locked={locked}>
                {upf.error ? <ErrorNotice error={upf.error} /> : null}
                {upf.isPending && <Skeleton h={80} radius="md" />}
                {upf.data && !upf.isPending && (
                  <Group align="flex-start" wrap="nowrap" gap="md">
                    <RingProgress
                      size={90}
                      thickness={9}
                      roundCaps
                      sections={[{ value: ((rating ?? 0) / 10) * 100, color: rating === undefined ? "gray" : rating <= 3 ? "lime" : rating <= 6 ? "yellow" : "red" }]}
                      label={
                        <Text ta="center" fw={800} fz={22}>
                          {rating ?? "–"}
                        </Text>
                      }
                      style={{ flex: "none" }}
                    />
                    <Stack gap={4} style={{ minWidth: 0 }}>
                      <Text fz="xs" c="dimmed">
                        1 = minimally processed · 10 = ultra-processed
                      </Text>
                      {upf.data.data.highlightedIngredients?.length ? (
                        <Group gap={4}>
                          {upf.data.data.highlightedIngredients.slice(0, 6).map((h) => (
                            <Badge key={h} color="orange" variant="light" tt="none" size="sm">
                              {h}
                            </Badge>
                          ))}
                        </Group>
                      ) : null}
                      {upf.data.data.chainOfThought && (
                        <Text fz="sm" lineClamp={4}>
                          {upf.data.data.chainOfThought}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                )}
                {!upf.data && !upf.isPending && !upf.error && (
                  <Text fz="sm" c="dimmed">
                    The product's own ingredient list goes in, so additives and processing steps on the label drive the score.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "Blood sugar impact",
            body: (
              <Step title="Glycemic index and load" route="POST /napi/tools/estimateGlycemicIndexLoad" to="/insights" done={Boolean(gi.data)} locked={locked}>
                {gi.error ? <ErrorNotice error={gi.error} /> : null}
                {gi.isPending && <Skeleton h={60} radius="md" />}
                {gi.data && !gi.isPending && (
                  <Stack gap={4}>
                    {(gi.data.data.ingredients ?? []).map((r, i) => (
                      <Group key={i} justify="space-between" wrap="nowrap">
                        <Text fz="sm" fw={600} truncate>
                          {r.name}
                        </Text>
                        <Text fz="sm" ff="monospace">
                          GI {fmtNumber(mid(r.gi_low, r.gi_high))} · GL {fmtNumber(mid(r.gl_low, r.gl_high), 1)}
                        </Text>
                      </Group>
                    ))}
                    {!giRow && <Text c="dimmed">No estimate returned.</Text>}
                  </Stack>
                )}
                {!gi.data && !gi.isPending && !gi.error && (
                  <Text fz="sm" c="dimmed">
                    Estimated glycemic index and load ranges for the product, for GLP-1, diabetes and blood-sugar features.
                  </Text>
                )}
              </Step>
            ),
          },
          {
            label: "Does it fit the user's goal?",
            body: (
              <Step title="Rate against any criterion" route="POST /napi/tools/genericFoodRating" to="/insights" done={Boolean(rate.data)} locked={locked}>
                {rate.error ? <ErrorNotice error={rate.error} /> : null}
                {rate.isPending && <Skeleton h={100} radius="md" />}
                {rate.data && !rate.isPending && <GoalRatingResult data={rate.data.data} criterion={rate.variables?.goal} size={100} />}
                {!rate.data && !rate.isPending && !rate.error && (
                  <Text fz="sm" c="dimmed">
                    The same product text, rated 0–10 against the goal above, with the reasoning a user can read.
                  </Text>
                )}
              </Step>
            ),
          },
        ]}
      />
    </Stack>
  );
}
